/**
 * Global Error Handler Utility
 * Provides centralized error handling for the application
 */

import { unauthorizedHandler } from './UnauthorizedHandler';

export interface ErrorHandlerConfig {
  enableConsoleLogging?: boolean;
  enableRecaptchaHandling?: boolean;
  enableTimeoutHandling?: boolean;
  enableUnauthorizedHandling?: boolean;
  customHandlers?: Array<(error: any) => boolean>;
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private config: ErrorHandlerConfig;
  private isInitialized = false;

  private constructor(config: ErrorHandlerConfig = {}) {
    this.config = {
      enableConsoleLogging: true,
      enableRecaptchaHandling: true,
      enableTimeoutHandling: true,
      enableUnauthorizedHandling: true,
      ...config
    };
  }

  public static getInstance(config?: ErrorHandlerConfig): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler(config);
    }
    return ErrorHandler.instance;
  }

  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    // Initialize unauthorized handler
    unauthorizedHandler.initialize();

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    
    // Handle uncaught errors
    window.addEventListener('error', this.handleError.bind(this));

    this.isInitialized = true;
  }

  public destroy(): void {
    if (!this.isInitialized) {
      return;
    }

    // Destroy unauthorized handler
    unauthorizedHandler.destroy();

    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    window.removeEventListener('error', this.handleError.bind(this));

    this.isInitialized = false;
  }

  private readonly rejectionHandlers: Array<{ enabled: keyof ErrorHandlerConfig; handler: (reason: any) => boolean; label: string }> = [
    { enabled: 'enableUnauthorizedHandling', handler: (r) => unauthorizedHandler.handleUnauthorized(r), label: '' },
    { enabled: 'enableRecaptchaHandling', handler: (r) => this.isRecaptchaTimeout(r), label: 'reCAPTCHA timeout' },
    { enabled: 'enableTimeoutHandling', handler: (r) => this.isTimeoutError(r), label: 'Request timeout' },
  ];

  private tryHandleUnhandledRejection(event: PromiseRejectionEvent): boolean {
    const matched = this.rejectionHandlers.find(
      h => this.config[h.enabled] && h.handler(event.reason)
    );
    if (matched) {
      if (matched.label) this.logError(`${matched.label} handled gracefully:`, event.reason);
      return true;
    }
    return this.handleCustomHandlersForReason(event.reason);
  }

  private handleCustomHandlersForReason(reason: any): boolean {
    if (!this.config.customHandlers) return false;
    for (const handler of this.config.customHandlers) {
      if (handler(reason)) return true;
    }
    return false;
  }

  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    if (this.tryHandleUnhandledRejection(event)) {
      event.preventDefault();
      return;
    }
    
    this.logError('Promise rejection handled:', event.reason);
    event.preventDefault();
  }

  private isResizeObserverError(errorMessage: string): boolean {
    return errorMessage.includes('ResizeObserver loop') || 
           errorMessage.includes('ResizeObserver loop completed with undelivered notifications');
  }

  private handleResizeObserverError(event: ErrorEvent): boolean {
    const errorMessage = event.message || (event.error && event.error.message) || '';
    if (this.isResizeObserverError(errorMessage)) {
      // These errors are benign and can be safely ignored
      event.preventDefault();
      return true;
    }
    return false;
  }

  private handleUnauthorizedError(event: ErrorEvent): boolean {
    if (this.config.enableUnauthorizedHandling && 
        unauthorizedHandler.handleUnauthorized(event.error)) {
      event.preventDefault();
      return true;
    }
    return false;
  }

  private handleRecaptchaError(event: ErrorEvent): boolean {
    if (this.config.enableRecaptchaHandling && this.isRecaptchaTimeout(event.error)) {
      this.logError('reCAPTCHA error handled gracefully:', event.error);
      event.preventDefault();
      return true;
    }
    return false;
  }

  private handleTimeoutError(event: ErrorEvent): boolean {
    if (this.config.enableTimeoutHandling && this.isTimeoutError(event.error)) {
      this.logError('Timeout error handled gracefully:', event.error);
      event.preventDefault();
      return true;
    }
    return false;
  }

  private handleCustomHandlers(event: ErrorEvent): boolean {
    if (this.config.customHandlers) {
      for (const handler of this.config.customHandlers) {
        if (handler(event.error)) {
          event.preventDefault();
          return true;
        }
      }
    }
    return false;
  }

  private handleError(event: ErrorEvent): void {
    const handlers = [
      () => this.handleResizeObserverError(event),
      () => this.handleUnauthorizedError(event),
      () => this.handleRecaptchaError(event),
      () => this.handleTimeoutError(event),
      () => this.handleCustomHandlers(event),
    ];

    for (const handler of handlers) {
      if (handler()) return;
    }
    
    this.logError('Error handled:', event.error);
    event.preventDefault();
  }

  private getErrorMessage(error: any): string | null {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    if (error.message) return error.message;
    return null;
  }

  private isRecaptchaTimeout(error: any): boolean {
    const message = this.getErrorMessage(error);
    return message ? this.checkRecaptchaTimeoutInString(message) : false;
  }

  private checkRecaptchaTimeoutInString(text: string): boolean {
    const TIMEOUT_U = 'Timeout (u)';
    const TIMEOUT_B = 'Timeout (b)';
    const RECAPTCHA_LOWER = 'recaptcha';
    const RECAPTCHA_UPPER = 'reCAPTCHA';
    
    return text.includes(TIMEOUT_U) ||
           text.includes(TIMEOUT_B) ||
           text.includes(RECAPTCHA_LOWER) ||
           text.includes(RECAPTCHA_UPPER);
  }

  private isTimeoutError(error: any): boolean {
    const message = this.getErrorMessage(error);
    return message ? this.checkTimeoutInString(message) : false;
  }

  private checkTimeoutInString(text: string): boolean {
    return text.includes('timeout') || 
           text.includes('Timeout') || 
           text.includes('ECONNABORTED');
  }

  private logError(message: string, error: any): void {
    if (this.config.enableConsoleLogging) {
      console.warn(message, error);
    }
  }

  public updateConfig(newConfig: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Auto-initialize in development
if (process.env.NODE_ENV === 'development') {
  errorHandler.initialize();
}

export { ErrorHandler };
export default ErrorHandler;
